import { Key, ReactNode, useEffect, useId, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { EmptyState } from './EmptyState'
import { ErrorState } from './ErrorState'
import { rowOpenProps } from './rowOpen'

export interface DataTableColumn<Row> {
  /** React key for the column. Defaults to its position. */
  id?: string
  header: ReactNode
  cell: (row: Row) => ReactNode
  align?: 'left' | 'center' | 'right'
  /** A CSS width, such as 120 (px) or '30%'. */
  width?: number | string
}

interface DataTableProps<Row> {
  /** Names the table for screen readers, for example "Clients". */
  label: string
  columns: DataTableColumn<Row>[]
  rows: Row[]
  rowKey: (row: Row) => Key
  /** Opens a row: on a click, or on Enter or Space when the row has focus. */
  onRowClick?: (row: Row) => void
  loading?: boolean
  /** Shown while loading instead of the default spinner. */
  loadingSlot?: ReactNode
  /** When true, `errorSlot` shows instead of the rows. */
  error?: boolean
  /** Usually an ErrorState with Retry. */
  errorSlot?: ReactNode
  /** Shown when the rows loaded and there are none, usually an EmptyState. */
  emptySlot?: ReactNode
  /** Shows each row as a card under 640 px wide, instead of a table that scrolls sideways. */
  renderCard?: (row: Row) => ReactNode
  /** The table never gets narrower than this; a narrower screen scrolls it sideways. */
  minWidth?: number
  className?: string
}

const CARD = 'rounded-2xl border border-border bg-surface'
const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' } as const
const CLICKABLE =
  'cursor-pointer hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary'

/** True while the media query matches. False where matchMedia doesn't exist, as in jsdom. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  )
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const list = window.matchMedia(query)
    const onChange = () => setMatches(list.matches)
    onChange()
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])
  return matches
}

/**
 * The shared list table. It scrolls sideways inside its card instead of
 * clipping columns, with the design spec's 680 px minimum width, and can show
 * each row as a card on phones instead. Loading, error and empty states take
 * the card's place, so they stay readable on a narrow screen.
 */
export function DataTable<Row>({
  label,
  columns,
  rows,
  rowKey,
  onRowClick,
  loading = false,
  loadingSlot,
  error = false,
  errorSlot,
  emptySlot,
  renderCard,
  minWidth = 680,
  className = '',
}: DataTableProps<Row>) {
  const captionId = useId()
  const narrow = useMediaQuery('(max-width: 639px)')

  if (loading || error || rows.length === 0) {
    let slot: ReactNode
    if (loading) {
      slot = loadingSlot ?? (
        <div role="status" className="flex items-center justify-center gap-2 px-6 py-10 text-sm text-foreground-muted">
          <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          Loading…
        </div>
      )
    } else if (error) {
      slot = errorSlot ?? <ErrorState />
    } else {
      slot = emptySlot ?? <EmptyState title="Nothing here yet" />
    }
    return <div className={`${CARD} ${className}`}>{slot}</div>
  }

  // Rows that open something are focusable and open on Enter or Space, as a
  // button would, so they work without a mouse.
  const openProps = (row: Row) => rowOpenProps(onRowClick ? () => onRowClick(row) : undefined)

  if (renderCard && narrow) {
    return (
      <ul aria-label={label} className={`${CARD} divide-y divide-border overflow-hidden ${className}`}>
        {rows.map((row) => (
          <li key={rowKey(row)} {...openProps(row)} className={`p-4 ${onRowClick ? CLICKABLE : ''}`}>
            {renderCard(row)}
          </li>
        ))}
      </ul>
    )
  }

  return (
    // Focusable, so the table can be scrolled sideways from the keyboard too.
    <div
      role="region"
      aria-labelledby={captionId}
      tabIndex={0}
      className={`${CARD} overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${className}`}
    >
      <table className="w-full border-collapse text-left text-[13.5px] text-foreground" style={{ minWidth }}>
        <caption id={captionId} className="sr-only">
          {label}
        </caption>
        <thead className="bg-surface-raised">
          <tr>
            {columns.map((column, index) => (
              <th
                key={column.id ?? index}
                scope="col"
                style={column.width != null ? { width: column.width } : undefined}
                className={`px-4 py-3 text-[11.5px] font-bold uppercase tracking-[0.4px] text-foreground-muted ${ALIGN[column.align ?? 'left']}`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} {...openProps(row)} className={`border-t border-border ${onRowClick ? CLICKABLE : ''}`}>
              {columns.map((column, index) => (
                <td key={column.id ?? index} className={`px-4 py-3 ${ALIGN[column.align ?? 'left']}`}>
                  {column.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
